import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useActions } from '../../../providers/actions-provider/actions.context';
import { assetIconCache } from '../../../../store/services/asset-icon-cache/asset-icon-cache';
import { validateIcon } from '../../../../utils/asset-icon/validate-icon';
import {
  MAX_ICON_BATCH_BYTES,
  MAX_ICON_BATCH_ENTRIES,
  MAX_ICONS_PER_RUN,
} from '../../../../utils/asset-type-import/constants';
import {
  IAssetIconBatchEntry, IAssetIconBatchResult,
} from '../../../../utils/asset-type-import/i-import-job';
import { readIconZip } from '../../../../utils/asset-type-import/read-icon-zip';
import { IIconProgress, TIconPhase } from './i-import-wizard';

/**
 * Uploads icons in batches. [AVP-891]
 *
 * Icons are matched to asset types by file name, server-side — the browser no
 * longer holds the list of codes the import created, so "no matching code" is
 * now an outcome the server reports rather than something guessed up front.
 */

export interface IRejectedIconFile {
  fileName: string;
  reason: string;
}

export interface IUseIconBatchUpload {
  phase: TIconPhase;
  files: File[];
  rejected: IRejectedIconFile[];
  progress: IIconProgress;
  results: IAssetIconBatchResult[];
  error: string | null;
  onFilesSelected: (files: File[]) => void;
  onZipSelected: (zip: File) => Promise<void>;
  upload: () => Promise<void>;
  reset: () => void;
}

export const stemOf = (name: string): string => {
  const base = name.split('/').pop() ?? name;
  const lastDot = base.lastIndexOf('.');
  return lastDot > 0 ? base.slice(0, lastDot) : base;
};

export const isOsNoise = (name: string): boolean => {
  const base = name.split('/').pop() ?? name;
  return base.startsWith('.') || name.includes('__MACOSX/')
    || base === 'Thumbs.db' || base === 'desktop.ini';
};

export interface IIconSelection {
  accepted: File[];
  rejected: IRejectedIconFile[];
}

/**
 * Validates, de-duplicates by code and caps the selection. Sorted first so the
 * winner of a contested code is the same whatever order the OS hands them over.
 */
export const selectIconFiles = (files: File[]): IIconSelection => {
  const accepted: File[] = [];
  const rejected: IRejectedIconFile[] = [];
  const taken = new Map<string, string>();

  [...files]
    .filter((file) => !isOsNoise(file.name))
    .sort((left, right) => left.name.localeCompare(right.name))
    .forEach((file) => {
      const check = validateIcon(file);
      if (!check.ok) {
        rejected.push({ fileName: file.name, reason: check.reason });
        return;
      }

      const key = stemOf(file.name).trim().toLowerCase();
      if (key === '') {
        rejected.push({ fileName: file.name, reason: 'The file name has no asset type code.' });
        return;
      }

      const already = taken.get(key);
      if (already !== undefined) {
        rejected.push({ fileName: file.name, reason: `${already} already covers this code.` });
        return;
      }

      if (accepted.length >= MAX_ICONS_PER_RUN) {
        rejected.push({
          fileName: file.name,
          reason: `Over the ${MAX_ICONS_PER_RUN}-icon limit for one run.`,
        });
        return;
      }

      taken.set(key, file.name);
      accepted.push(file);
    });

  return { accepted, rejected };
};

/** base64 without the data: prefix, which is what the endpoint expects. */
export const readAsBase64 = (file: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (): void => {
    const result = String(reader.result ?? '');
    const comma = result.indexOf(',');
    resolve(comma >= 0 ? result.slice(comma + 1) : result);
  };
  reader.onerror = (): void => reject(reader.error ?? new Error('Could not read the icon.'));
  reader.readAsDataURL(file);
});

/**
 * Packs entries by encoded size as well as count: 100 icons of 512 KB would be
 * 68 MB of base64 and bounce off the server's 8 MB body cap.
 */
export const packBatches = (entries: IAssetIconBatchEntry[]): IAssetIconBatchEntry[][] => {
  const batches: IAssetIconBatchEntry[][] = [];
  let current: IAssetIconBatchEntry[] = [];
  let bytes = 0;

  entries.forEach((entry) => {
    const size = entry.data.length;
    const full = current.length >= MAX_ICON_BATCH_ENTRIES
      || (current.length > 0 && bytes + size > MAX_ICON_BATCH_BYTES);

    if (full) {
      batches.push(current);
      current = [];
      bytes = 0;
    }

    current.push(entry);
    bytes += size;
  });

  if (current.length > 0) {
    batches.push(current);
  }
  return batches;
};

export const useIconBatchUpload = (): IUseIconBatchUpload => {
  const { assetActions } = useActions();
  const actions = useRef(assetActions);
  actions.current = assetActions;

  const [phase, setPhase] = useState<TIconPhase>('ready');
  const [files, setFiles] = useState<File[]>([]);
  const [rejected, setRejected] = useState<IRejectedIconFile[]>([]);
  const [progress, setProgress] = useState<IIconProgress>({ done: 0, total: 0 });
  const [results, setResults] = useState<IAssetIconBatchResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const onFilesSelected = useCallback((selected: File[]): void => {
    const selection = selectIconFiles(selected);
    setFiles(selection.accepted);
    setRejected(selection.rejected);
    setResults([]);
    setError(null);
    setPhase('ready');
  }, []);

  const onZipSelected = useCallback(async (zip: File): Promise<void> => {
    setError(null);
    try {
      onFilesSelected(await readIconZip(zip));
    } catch (zipError) {
      setFiles([]);
      setRejected([]);
      setError((zipError as Error).message || 'Could not read the archive.');
    }
  }, [onFilesSelected]);

  const upload = useCallback(async (): Promise<void> => {
    if (files.length === 0) {
      return;
    }

    setPhase('uploading');
    setProgress({ done: 0, total: files.length });
    setError(null);

    try {
      const entries: IAssetIconBatchEntry[] = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const data = await readAsBase64(file);
        entries.push({ code: stemOf(file.name).trim(), contentType: file.type || 'image/png', data });
      }

      const batches = packBatches(entries);
      const collected: IAssetIconBatchResult[] = [];
      let done = 0;

      // sequential: each batch holds one pooled connection on the server for a
      // couple of seconds, and the progress bar stays monotonic
      // eslint-disable-next-line no-restricted-syntax
      for (const batch of batches) {
        // eslint-disable-next-line no-await-in-loop
        const response = await actions.current.uploadAssetIconBatch(batch);
        collected.push(...(response?.results ?? []));
        done += batch.length;
        if (mounted.current) {
          setProgress({ done, total: files.length });
        }
      }

      assetIconCache.clear();

      if (mounted.current) {
        setResults(collected);
        setPhase('done');
      }
    } catch {
      if (mounted.current) {
        setError('Could not upload the icons. Any that already went up were saved.');
        setPhase('ready');
      }
    }
  }, [files]);

  const reset = useCallback((): void => {
    setFiles([]);
    setRejected([]);
    setResults([]);
    setProgress({ done: 0, total: 0 });
    setError(null);
    setPhase('ready');
  }, []);

  return {
    phase,
    files,
    rejected,
    progress,
    results,
    error,
    onFilesSelected,
    onZipSelected,
    upload,
    reset,
  };
};

export default useIconBatchUpload;
